import { Globe, Clock, File, HardDrive, User, Users } from 'lucide-react';
import type { Friend } from '@omokage/shared';
import { getWorldUrl } from '@omokage/shared';
import type { PresencePhase } from '@/hooks/usePresence';
import { MetadataItem } from './MetadataItem';
import { PlayersSection } from './PlayersSection';
import { formatFileSize, formatDate } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import type { Photo } from '@/types/photo';

/**
 * PhotoMetadataPanel - Display photo metadata in a responsive panel
 *
 * Features:
 * - Responsive layout (right sidebar on desktop, bottom sheet on mobile)
 * - Framer Motion slide-in animation
 * - VRChat-specific metadata (world, taken date)
 * - File metadata (filename, size, resolution)
 * - Accessibility support with ARIA attributes
 *
 * Desktop: Fixed right panel (320px wide)
 * Mobile: Bottom sheet (max 35vh height, scrollable)
 *
 * Performance:
 * - Only renders when visible
 * - Lightweight formatters
 *
 * Accessibility:
 * - Semantic HTML structure
 * - ARIA labels for screen readers
 * - Keyboard navigation support
 */
export interface PhotoMetadataPanelProps {
  /** Photo to display metadata for */
  photo: Photo;
  /** Optional CSS class name */
  className?: string;
  /** Callback when player is removed */
  onPlayerRemoved?: (vrchatId: string) => void;
  /** Callback when player is added */
  onPlayerAdded?: (player: Friend) => void;
  /** Read-only mode (hides add/remove buttons) */
  readOnly?: boolean;
  /** Current presence phase for enter/exit animation */
  presencePhase?: PresencePhase;
}

export function PhotoMetadataPanel({
  photo,
  className = '',
  onPlayerRemoved,
  onPlayerAdded,
  readOnly = false,
  presencePhase = 'entered',
}: PhotoMetadataPanelProps) {
  return (
    <aside
      data-presence={presencePhase}
      className={cn(
        'fixed z-50 overflow-y-auto bg-card/95 backdrop-blur-md custom-scrollbar',
        'bottom-0 left-0 right-0 max-h-[35vh] rounded-t-2xl border-t border-border',
        'transition-all duration-300 ease-out motion-reduce:transform-none motion-reduce:opacity-100 motion-reduce:transition-none',
        'md:top-0 md:left-0 md:h-full md:max-h-full md:w-80 md:rounded-none md:border-t-0 md:border-r',
        presencePhase === 'entered'
          ? 'translate-y-0 opacity-100 md:translate-x-0'
          : 'translate-y-5 opacity-0 md:-translate-x-5 md:translate-y-0',
        className
      )}
      role="complementary"
      aria-label="Photo metadata"
    >
      <div className="p-6 space-y-1">
        {/* Header */}
        <h3 className="text-lg font-semibold text-foreground mb-4">Photo Details</h3>

        {/* VRChat Information */}
        {(photo.worldName ||
          photo.authorName ||
          photo.takenAt ||
          photo.instanceType ||
          photo.region) && (
          <div className="space-y-1 pb-4 border-b border-border">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
              VRChat Info
            </h4>

            {photo.worldName && (
              <MetadataItem
                icon={<Globe className="w-4 h-4" />}
                label="World Name"
                value={photo.worldName}
                href={photo.worldId ? getWorldUrl(photo.worldId) : undefined}
              />
            )}

            {photo.authorName && (
              <MetadataItem
                icon={<User className="w-4 h-4" />}
                label="Author"
                value={photo.authorName}
              />
            )}

            {photo.takenAt && (
              <MetadataItem
                icon={<Clock className="w-4 h-4" />}
                label="Taken At"
                value={formatDate(photo.takenAt)}
              />
            )}

            {photo.instanceType && (
              <MetadataItem
                icon={<Users className="w-4 h-4" />}
                label="Instance Type"
                value={photo.instanceType}
              />
            )}

            {photo.region && (
              <MetadataItem
                icon={<Globe className="w-4 h-4" />}
                label="Region"
                value={photo.region.toUpperCase()}
              />
            )}
          </div>
        )}

        {/* Players Section */}
        <div className="py-4 border-b border-border">
          <PlayersSection
            photoId={photo.id}
            players={photo.friends || []}
            onPlayerRemoved={onPlayerRemoved}
            onPlayerAdded={onPlayerAdded}
            readOnly={readOnly}
          />
        </div>

        {/* File Information */}
        <div className="space-y-1 pt-4">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            File Info
          </h4>

          <MetadataItem
            icon={<File className="w-4 h-4" />}
            label="Filename"
            value={photo.originalFilename || photo.filename || 'Unknown'}
          />

          {photo.fileSize && (
            <MetadataItem
              icon={<HardDrive className="w-4 h-4" />}
              label="File Size"
              value={formatFileSize(photo.fileSize)}
            />
          )}
        </div>
      </div>
    </aside>
  );
}
